// Interface Candidat Complète selon le cahier des charges
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  FiHome, FiFileText, FiMessageCircle, FiFolder,
  FiTrendingUp, FiAlertCircle, FiCheck, FiClock
} from 'react-icons/fi';
import { theme } from '@theme';
import { useSession } from '@/contexts/SessionContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  ApplicationCard,
  ApplicationCardData,
  StatusBadge,
  ScoreDisplay,
} from '@/components/applications';
import { ApplicationServices } from '@/services/ApplicationServices';
import { ApplicationWithRelations } from '@/models';

// Types
interface CandidatureStatus {
  id: string;
  logementType: string;
  adresse: string;
  status: 'draft' | 'submitted' | 'verification' | 'scored' | 'approved' | 'rejected';
  score?: number;
  dateSubmitted?: string;
  nextStep?: string;
}

// Styled Components
const WelcomeSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const WelcomeTitle = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const WelcomeSubtitle = styled.p`
  color: ${theme.colors.text}80;
  font-size: ${theme.typography.sizes.base};
`;

const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes.xl};
  font-weight: bold;
  color: ${theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const CandidaturesList = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${theme.spacing.xl};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ActionButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: 6px;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.sizes.lg};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}10;
  border: 1px solid ${theme.colors.error}40;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.error};
  margin-bottom: ${theme.spacing.lg};
`;

// Component Principal
export const DashboardCandidat: React.FC = () => {
  const { loggedUser: user } = useSession();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ApplicationWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load user's applications
  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApplicationServices.my_applications();
      const result = response as {
        success: boolean;
        data?: ApplicationWithRelations[];
      };

      if (result.success && result.data) {
        setApplications(result.data);
      } else {
        setApplications([]);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des candidatures:', err);
      setError('Impossible de charger vos candidatures. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getHousingInfo = (app: ApplicationWithRelations): string => {
    const housing = typeof app.housing === 'object' ? app.housing : null;
    if (!housing) return 'Logement';
    return `${housing.type || 'Logement'} - ${housing.city || ''}`;
  };

  const getAddress = (app: ApplicationWithRelations): string => {
    const housing = typeof app.housing === 'object' ? app.housing : null;
    if (!housing) return 'Adresse non disponible';
    return `${housing.address || ''}, ${housing.city || ''}`.trim();
  };

  const getNextStep = (status: string | undefined): string => {
    switch (status) {
      case 'draft':
        return 'Complétez votre dossier';
      case 'submitted':
        return 'En attente de vérification';
      case 'verification':
        return 'Vérification des documents en cours';
      case 'verified':
        return 'En attente de notation';
      case 'approved':
        return 'Dossier approuvé';
      case 'rejected':
        return 'Dossier rejeté';
      default:
        return 'En cours de traitement';
    }
  };

  // Calculate stats from real data
  const stats = {
    total: applications.length,
    enCours: applications.filter(app =>
      ['submitted', 'verification', 'verified'].includes(app.status || '')
    ).length,
    approuvees: applications.filter(app => app.status === 'approved').length,
    scoreMoving: applications.length > 0
      ? Math.round(
          applications.reduce((acc, app) => acc + (app.score || 0), 0) / applications.length
        )
      : 0
  };

  return (
    <DashboardLayout>
      {error && (
        <ErrorMessage>
          <FiAlertCircle size={24} />
          <div>{error}</div>
        </ErrorMessage>
      )}

      <WelcomeSection>
        <WelcomeTitle>🏠 Bienvenue, {user?.first_name || 'Candidat'}</WelcomeTitle>
        <WelcomeSubtitle>
          Suivez vos candidatures et gérez vos documents depuis votre espace personnel P-Screen
        </WelcomeSubtitle>
      </WelcomeSection>

      <QuickStats>
        <StatCard>
          <StatIcon color={theme.colors.info}>
            <FiFileText />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Candidatures totales</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color={theme.colors.warning}>
            <FiClock />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.enCours}</StatValue>
            <StatLabel>En cours de traitement</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color={theme.colors.success}>
            <FiCheck />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.approuvees}</StatValue>
            <StatLabel>Approuvées</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color={theme.colors.primary}>
            <FiTrendingUp />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.scoreMoving}/100</StatValue>
            <StatLabel>Score moyen</StatLabel>
          </StatContent>
        </StatCard>
      </QuickStats>

      <CandidaturesList>
        <h2 style={{ marginBottom: theme.spacing.lg }}>Mes Candidatures</h2>

        {loading ? (
          <LoadingMessage>Chargement de vos candidatures...</LoadingMessage>
        ) : applications.length > 0 ? (
          applications.map(app => {
            // Convertir le format de candidature vers ApplicationCardData
            const applicationData: ApplicationCardData = {
              id: app.id?.toString() || '',
              housing_type_name: getHousingInfo(app),
              adresse: getAddress(app),
              status: app.status as any,
              score: app.score,
              dateSubmitted: app.created_at,
              nextStep: getNextStep(app.status),
            };

            return (
              <ApplicationCard
                key={app.id}
                application={applicationData}
                onViewDetails={() => navigate(`/applications/${app.id}`)}
                onEdit={() => navigate(`/applications/${app.id}/edit`)}
                showProgress={true}
              />
            );
          })
        ) : (
          <EmptyState>
            <FiFolder size={48} />
            <h3>Aucune candidature</h3>
            <p>Commencez par créer votre première candidature</p>
            <ActionButton
              style={{ marginTop: theme.spacing.lg }}
              onClick={() => navigate('/new-application')}
            >
              Nouvelle candidature
            </ActionButton>
          </EmptyState>
        )}
      </CandidaturesList>
    </DashboardLayout>
  );
};
